import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/components/utils/useLanguage';
import { useCategories } from '@/hooks/useSharedData';
import { HERO_IMAGE } from '@/components/home/heroImage';
import { filtersToParams } from '@/utils/filterUrlParams';

export default function HeroSection() {
  const { language, t } = useLanguage();
  const { data: categories = [] } = useCategories();

  const quickChips = useMemo(() => {
    const allChips = [
      { label: t('Trotz Regen', 'Despite the rain'), filters: { weather_suitable: ['rainy'] } },
      { label: t('Kostenlos', 'Free'), filters: { weitere_filter: ['kostenlos'] } },
      { label: t('Geheimtipps', 'Hidden Gem'), filters: { activity_class: ['geheimtipp'] } },
      { label: t('Am Wochenende', 'On weekends'), filters: { tage: ['samstag', 'sonntag'] } },
      { label: t('Familien-getestet', 'Family-tested'), filters: { weitere_filter: ['familien-getestet'] } },
      { label: t('Mit Kleinkind', 'With a toddler'), filters: { alter: ['1-3'] } },
      { label: t('Gegen Langeweile', 'Fight boredom'), filters: { intensity: ['aktiv'] } },
      { label: t('Wasserspass', 'Water fun'), filters: { kategorie: categories.filter(c => ['gartenbad','schwimmbad'].includes(c.slug)).map(c => c.slug) } },
      { label: t('Neues lernen', 'Learn something new'), filters: { kategorie: categories.filter(c => c.slug === 'lernen-entdecken').map(c => c.slug) } },
    ];
    return [...allChips].sort(() => Math.random() - 0.5).slice(0, 3);
  }, [categories, language, t]);

  return (
    <section className="relative -mt-[88px] md:-mt-[112px] pt-[88px] md:pt-[112px]">
      {/* Background image — <img> with fetchpriority for LCP discoverability */}
      <img
        src={HERO_IMAGE.src}
        srcSet={HERO_IMAGE.srcSet}
        sizes="100vw"
        alt=""
        aria-hidden="true"
        fetchpriority="high"
        loading="eager"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        alt="Familie im Kannenfeldpark in Basel – Ausflüge mit Kindern"
      />
      {/* Radial gradient overlay for text contrast + emerald tint to keep brand tone */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(6,78,59,0.35) 0%, rgba(6,78,59,0.65) 55%, rgba(4,47,30,0.85) 100%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center">
          <h1
            style={{ animationDelay: '0.1s' }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 leading-tight animate-fade-in-up"
          >
            {t('Ausflüge mit Kindern in Basel', 'Family Excursions in Basel')}
          </h1>
          <p
            style={{ animationDelay: '0.2s' }}
            className="text-xl text-white max-w-2xl mx-auto animate-fade-in-up"
          >
            {t('Entdeck den nächsten Familienausflug in Basel und Region, der perfekt zu euch passt', 'Discover your next family trip that perfectly suits you')}
          </p>
          <div
            style={{ animationDelay: '0.4s' }}
            className="flex flex-wrap gap-2 justify-center mt-6 animate-fade-in-up"
          >
            {quickChips.map(({ label, filters }) => (
              <Link key={label} to={`/ausflugsideen?${filtersToParams(filters).toString()}`}>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-white hover:bg-emerald-100 text-emerald-600 hover:text-emerald-900 text-m font-medium transition-colors cursor-pointer border border-white/30 backdrop-blur-sm">
                  {label}
                </span>
              </Link>
            ))}
            <Link to="/ausflugsideen" className="chevron-after inline-flex items-center px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-m font-medium transition-colors cursor-pointer">
                {t('Alle ansehen', 'View all')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}